import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, FileText, Download, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_285b778d-268b-4f9f-806e-ab953b551b56/artifacts/8tp297eu_Enable%20Healthcare%20emblem_BLUE.png";

export default function ContractSign() {
  const { id } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signerName, setSignerName] = useState("");
  const [signed, setSigned] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchContract defined below; re-run only when contract id changes
  }, [id]);

  const fetchContract = async () => {
    try {
      const res = await axios.get(`${API}/contracts/${id}`);
      setContract(res.data);
      if (res.data.status === "sent") {
        await axios.put(`${API}/contracts/${id}`, { status: "viewed", viewed_at: new Date().toISOString() }).catch(() => {});
      }
      if (res.data.status === "signed") setSigned(true);
    } catch { }
    setLoading(false);
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    let isDrawing = false;
    canvas.onmousedown = (e) => { isDrawing = true; ctx.beginPath(); ctx.moveTo(e.offsetX, e.offsetY); };
    canvas.onmousemove = (e) => { if (isDrawing) { ctx.lineTo(e.offsetX, e.offsetY); ctx.stroke(); } };
    canvas.onmouseup = () => { isDrawing = false; };
    canvas.onmouseleave = () => { isDrawing = false; };
  };

  useEffect(() => { if (canvasRef.current) initCanvas(); }, [contract]);

  const handleSign = async () => {
    if (!signerName.trim()) { toast.error("Please enter your full name"); return; }
    const sigData = canvasRef.current?.toDataURL() || "";
    try {
      await axios.post(`${API}/contracts/${id}/sign`, {
        signer_name: signerName, signature_data: sigData, signer_ip: "client",
      });
      setSigned(true);
      toast.success("Contract signed successfully!");
    } catch { toast.error("Failed to sign contract"); }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) { const ctx = canvas.getContext("2d"); ctx.clearRect(0, 0, canvas.width, canvas.height); }
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f4f1" }}>
      <div style={{ width: 28, height: 28, border: "2.5px solid #2563eb", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!contract) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f4f1" }}>
      <div style={{ textAlign: "center" }}>
        <AlertTriangle style={{ width: 40, height: 40, color: "#888", margin: "0 auto 12px" }} />
        <div style={{ fontSize: 16, fontWeight: 500 }}>Contract not found</div>
        <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>This link may be invalid or expired.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f5f4f1", padding: "40px 20px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <img src={LOGO_URL} alt="Enable Healthcare" style={{ height: 36 }} />
          <div><div style={{ fontSize: 16, fontWeight: 600 }}>Enable Healthcare</div><div style={{ fontSize: 11, color: "#888" }}>Service Agreement</div></div>
        </div>

        {signed ? (
          <div style={{ background: "#fff", borderRadius: 12, border: "0.5px solid #eee", padding: 32, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#EAF3DE", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Check style={{ width: 28, height: 28, color: "#3B6D11" }} />
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Contract Signed</div>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
              {contract.contract_number} was signed by {contract.signer_name || signerName} on {contract.signed_at ? new Date(contract.signed_at).toLocaleDateString() : new Date().toLocaleDateString()}
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#0F6E56", marginBottom: 20 }}>
              ${(contract.total || 0).toLocaleString()}/mo · {contract.lead_title || contract.title}
            </div>
            <button onClick={() => window.print()} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 20px", borderRadius: 8, border: "0.5px solid #eee", background: "#fff", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
              <Download style={{ width: 14, height: 14 }} /> Download signed contract
            </button>
          </div>
        ) : (
          <div style={{ background: "#fff", borderRadius: 12, border: "0.5px solid #eee", overflow: "hidden" }}>
            {/* Contract header */}
            <div style={{ padding: "20px 28px", borderBottom: "0.5px solid #eee" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{contract.title || "Service Agreement"}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{contract.contract_number} · Created {contract.created_at ? new Date(contract.created_at).toLocaleDateString() : ""}</div>
                </div>
                <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 500, background: "#E6F1FB", color: "#185FA5" }}>Awaiting signature</span>
              </div>
            </div>

            {/* Parties */}
            <div style={{ padding: "20px 28px", borderBottom: "0.5px solid #eee", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.4px", marginBottom: 6 }}>Service Provider</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Enable Healthcare Inc.</div>
                <div style={{ fontSize: 12, color: "#888" }}>Healthcare Technology Solutions</div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.4px", marginBottom: 6 }}>Client</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{contract.contact_name || "—"}</div>
                <div style={{ fontSize: 12, color: "#888" }}>{contract.lead_title || "—"}</div>
              </div>
            </div>

            {/* Line items */}
            <div style={{ padding: "20px 28px", borderBottom: "0.5px solid #eee" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.4px", marginBottom: 10 }}>Products & Services</div>
              {(contract.line_items || []).map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < (contract.line_items || []).length - 1 ? "0.5px solid #f5f5f5" : "none" }}>
                  <div><div style={{ fontSize: 13, fontWeight: 500 }}>{item.product || item.name}</div><div style={{ fontSize: 11, color: "#888" }}>{item.description || ""}</div></div>
                  <div style={{ fontSize: 13, fontWeight: 500, textAlign: "right" }}>${(item.total || item.unit_price || 0).toLocaleString()}{item.unit_label || "/mo"}</div>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 0", marginTop: 8, borderTop: "1px solid #eee" }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Total</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#0F6E56" }}>${(contract.total || 0).toLocaleString()}/mo</span>
              </div>
            </div>

            {/* Terms */}
            <div style={{ padding: "20px 28px", borderBottom: "0.5px solid #eee" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.4px", marginBottom: 10 }}>Terms & Conditions</div>
              <div style={{ fontSize: 12, color: "#555", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{contract.terms || "Standard terms apply."}</div>
            </div>

            {/* Signature section */}
            <div style={{ padding: "24px 28px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.4px", marginBottom: 12 }}>Electronic Signature</div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>Full legal name</label>
                <input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Type your full name..." data-testid="signer-name"
                  style={{ width: "100%", maxWidth: 320, padding: "8px 12px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 500 }}>Draw your signature</label>
                  <button onClick={clearSignature} style={{ fontSize: 11, color: "#888", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Clear</button>
                </div>
                <canvas ref={canvasRef} width={400} height={100} data-testid="signature-canvas"
                  style={{ border: "0.5px dashed #bbb", borderRadius: 8, cursor: "crosshair", display: "block", background: "#fafafa" }} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button onClick={handleSign} data-testid="sign-contract-btn"
                  style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
                  Accept & Sign Contract
                </button>
                <button onClick={() => window.print()} style={{ padding: "10px 20px", borderRadius: 8, border: "0.5px solid #ddd", background: "#fff", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  <Download style={{ width: 14, height: 14, display: "inline", verticalAlign: "middle", marginRight: 4 }} />Download PDF
                </button>
              </div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 12 }}>
                By clicking "Accept & Sign Contract" you agree to the terms above. This constitutes a legally binding electronic signature.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
